// API configuration and services
import { settings } from "../config/settings";
import {
  AUTH_ERROR_CODES,
  AUTH_ERROR_STORAGE_KEY,
} from "../utils/authErrors";

const API_BASE_URL = settings.API_BASE_URL;
const CSRF_COOKIE_NAME = "csrftoken";
const UNSAFE_HTTP_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// Create axios instance with default config
const api = {
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
};

const getCookie = (name) => {
  if (typeof document === "undefined" || !document.cookie) {
    return null;
  }

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.trim().split("=");
    if (key === name) {
      return decodeURIComponent(rest.join("="));
    }
  }
  return null;
};

const ensureCsrfCookie = async () => {
  if (getCookie(CSRF_COOKIE_NAME)) {
    return;
  }

  try {
    await fetch(`${api.baseURL}/auth/csrf/`, {
      method: "GET",
      credentials: "include",
    });
  } catch {
    // Cookie may already exist from another request; continue either way.
  }
};

// Helper function to make API requests
const makeRequest = async (endpoint, options = {}) => {
  const url = `${api.baseURL}${endpoint}`;
  const method = (options.method || "GET").toUpperCase();

  if (UNSAFE_HTTP_METHODS.has(method)) {
    await ensureCsrfCookie();
  }

  const isFormData = options.body instanceof FormData;

  // Prepare headers
  const headers = {
    ...api.headers,
    ...options.headers,
  };

  const csrfToken = getCookie(CSRF_COOKIE_NAME);
  if (csrfToken) {
    headers["X-CSRFToken"] = csrfToken;
  }

  if (isFormData) {
    delete headers["Content-Type"];
  }

  // Prepare body
  let body = options.body;
  if (body && typeof body === "object" && !isFormData) {
    body = JSON.stringify(body);
    headers["Content-Type"] = "application/json";
  }

  const { headers: _ignoredHeaders, body: _ignoredBody, ...restOptions } = options;

  const config = {
    method,
    credentials: "include", // Include cookies for session authentication
    ...restOptions,
    headers,
    body,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorData = {};
      try {
        errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          if (typeof errorData.detail === "object") {
            errorMessage =
              errorData.detail.detail_ar ||
              errorData.detail.detail ||
              errorMessage;
          } else {
            errorMessage = errorData.detail;
          }
        } else if (errorData.detail_ar) {
          errorMessage = errorData.detail_ar;
        } else if (typeof errorData === "object" && !Array.isArray(errorData)) {
          const fieldMessages = Object.values(errorData).flat();
          if (fieldMessages.length > 0) {
            const first = fieldMessages[0];
            errorMessage = Array.isArray(first) ? first[0] : String(first);
          }
        }
      } catch (e) {
        // If we can't parse the error response, use the status text
        errorMessage = response.statusText || errorMessage;
      }

      const isInactiveUser =
        errorData.code === AUTH_ERROR_CODES.USER_INACTIVE ||
        errorMessage.includes("إيقاف تنشيط المستخدم");

      // Handle authentication errors
      if (
        response.status === 401 ||
        (response.status === 403 && isInactiveUser)
      ) {
        localStorage.removeItem(settings.STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(settings.STORAGE_KEYS.USER_DATA);
        localStorage.removeItem(settings.STORAGE_KEYS.USER_PERMISSIONS);
        if (isInactiveUser) {
          sessionStorage.setItem(
            AUTH_ERROR_STORAGE_KEY,
            AUTH_ERROR_CODES.USER_INACTIVE
          );
        }
      }

      const apiError = new Error(errorMessage);
      apiError.status = response.status;
      apiError.data = errorData;
      if (isInactiveUser) {
        apiError.code = AUTH_ERROR_CODES.USER_INACTIVE;
      } else if (errorData.code) {
        apiError.code = errorData.code;
      }
      throw apiError;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

// Authentication API
export const authAPI = {
  login: async (credentials) => {
    return makeRequest("/auth/login/", {
      method: "POST",
      body: credentials,
    });
  },

  logout: async () => {
    return makeRequest("/auth/logout/", {
      method: "POST",
    });
  },

  refreshToken: async () => {
    return makeRequest("/auth/refresh/", {
      method: "POST",
    });
  },

  updateSiteStyle: async (siteStyle) => {
    return makeRequest("/auth/site-style/", {
      method: "PATCH",
      body: { site_style: siteStyle },
    });
  },

  getProfile: async () => {
    return makeRequest("/auth/profile/");
  },

  checkAuth: async () => {
    return makeRequest("/auth/profile/");
  },

  ensureCsrf: ensureCsrfCookie,
};

// Tickets API
export const ticketsAPI = {
  getTickets: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/tickets/?${queryString}` : "/tickets/";
    return makeRequest(endpoint);
  },

  getTicket: async (id) => {
    return makeRequest(`/tickets/${id}/`);
  },

  createTicket: async (ticketData) => {
    return makeRequest("/tickets/", {
      method: "POST",
      body: ticketData,
    });
  },

  updateTicket: async (id, ticketData) => {
    return makeRequest(`/tickets/${id}/`, {
      method: "PATCH",
      body: ticketData,
    });
  },

  deleteTicketAttachment: async (attachmentId) => {
    return makeRequest(`/ticket-attachments/${attachmentId}/`, {
      method: "DELETE",
    });
  },

  addTicketAttachment: async (ticketId, file) => {
    const formData = new FormData();
    formData.append("ticket", ticketId);
    formData.append("file", file);
    formData.append("filename", file.name);

    return makeRequest("/ticket-attachments/", {
      method: "POST",
      body: formData,
    });
  },

  deleteTicket: async (id) => {
    return makeRequest(`/tickets/${id}/`, {
      method: "DELETE",
    });
  },

  getOpenTickets: async (page = 1, pageSize = 20, communicationFilter = "all") => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    if (communicationFilter && communicationFilter !== "all") {
      params.set("communication_filter", communicationFilter);
    }
    return makeRequest(`/tickets/open_tickets/?${params}`);
  },

  getClosedTickets: async (page = 1, pageSize = 20, communicationFilter = "all") => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    if (communicationFilter && communicationFilter !== "all") {
      params.set("communication_filter", communicationFilter);
    }
    return makeRequest(`/tickets/closed_tickets/?${params}`);
  },

  getTicketsStatistics: async () => {
    return makeRequest("/statistics/");
  },

  searchTickets: async (params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
      )
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    const endpoint = queryString ? `/tickets/?${queryString}` : "/tickets/";
    return makeRequest(endpoint);
  },

  exportTicketsExcel: async (params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
      )
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    const endpoint = queryString
      ? `/tickets/export_excel/?${queryString}`
      : "/tickets/export_excel/";
    const response = await fetch(`${api.baseURL}${endpoint}`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const data = await response.json();
        message = data.error || data.detail || message;
      } catch {
        // Keep the status fallback when the body is not JSON.
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
    const filename = filenameMatch?.[1] || "tickets.xlsx";
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  },

  getUserSearchOptions: async () => {
    return makeRequest("/user-profiles/search_options/");
  },

  // Ticket replies/comments
  getTicketReplies: async (ticketId) => {
    return makeRequest(`/tickets/${ticketId}/replies/`);
  },

  addTicketReply: async (ticketId, replyData) => {
    return makeRequest(`/tickets/${ticketId}/add_reply/`, {
      method: "POST",
      body: replyData,
    });
  },

  addReplyAttachment: async (replyId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("reply_id", replyId);

    return makeRequest(`/reply-attachments/add_to_reply/`, {
      method: "POST",
      body: formData,
      headers: {
        // Don't set Content-Type, let browser set it with boundary
      },
    });
  },

  // Close and reopen tickets
  closeTicket: async (ticketId) => {
    return makeRequest(`/tickets/${ticketId}/close_ticket/`, {
      method: "POST",
    });
  },

  reopenTicket: async (ticketId) => {
    return makeRequest(`/tickets/${ticketId}/reopen_ticket/`, {
      method: "POST",
    });
  },

  classifyComplaint: async (ticketId, classification) => {
    return makeRequest(`/tickets/${ticketId}/classify_complaint/`, {
      method: "POST",
      body: { classification },
    });
  },

  getReplyDeletePreview: async (ticketId, replyId) => {
    return makeRequest(
      `/tickets/${ticketId}/replies/${replyId}/delete_preview/`
    );
  },

  deleteTicketReply: async (ticketId, replyId, reason) => {
    return makeRequest(`/tickets/${ticketId}/replies/${replyId}/`, {
      method: "DELETE",
      body: { reason },
    });
  },

  downloadAttachment: async (attachment) => {
    const id = attachment?.attachment_id;
    if (!id) {
      throw new Error("Missing attachment id");
    }
    const endpoint = attachment.reply
      ? `/reply-attachments/${id}/download/`
      : `/ticket-attachments/${id}/download/`;
    await ensureCsrfCookie();
    const headers = {};
    const csrfToken = getCookie(CSRF_COOKIE_NAME);
    if (csrfToken) {
      headers["X-CSRFToken"] = csrfToken;
    }
    const response = await fetch(`${api.baseURL}${endpoint}`, {
      method: "GET",
      credentials: "include",
      headers,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = attachment.filename || "attachment";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  },

  // Get reference data for forms
  getCommunicationMethods: async () => {
    return makeRequest("/communication-methods/");
  },

  getContactReasons: async () => {
    return makeRequest("/contact-reasons/");
  },

  getShortSubjects: async () => {
    return makeRequest("/short-subjects/");
  },

  getBranches: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/branches/?${queryString}` : "/branches/";
    return makeRequest(endpoint);
  },

  getProducts: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/products/?${queryString}` : "/products/";
    return makeRequest(endpoint);
  },

  getDepartments: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/departments/?${queryString}`
      : "/departments/";
    return makeRequest(endpoint);
  },

  getCustomers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/customers/?${queryString}` : "/customers/";
    return makeRequest(endpoint);
  },

  // Get adjacent tickets for navigation
  getAdjacentTickets: async (currentTicketId, limit = 10) => {
    return makeRequest(`/tickets/${currentTicketId}/adjacent/?limit=${limit}`);
  },
};

// Customers API
export const customersAPI = {
  getCustomers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/customers/?${queryString}` : "/customers/";
    return makeRequest(endpoint);
  },

  getCustomer: async (id) => {
    return makeRequest(`/customers/${id}/`);
  },

  createCustomer: async (customerData) => {
    return makeRequest("/customers/", {
      method: "POST",
      body: customerData,
    });
  },

  updateCustomer: async (id, customerData) => {
    return makeRequest(`/customers/${id}/`, {
      method: "PUT",
      body: customerData,
    });
  },

  deleteCustomer: async (id) => {
    return makeRequest(`/customers/${id}/`, {
      method: "DELETE",
    });
  },
};

const createCrudAPI = (basePath, idField) => ({
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `${basePath}?${queryString}` : basePath;
    return makeRequest(endpoint);
  },
  get: async (id) => makeRequest(`${basePath}${id}/`),
  create: async (data) =>
    makeRequest(basePath, { method: "POST", body: data }),
  update: async (id, data) =>
    makeRequest(`${basePath}${id}/`, { method: "PUT", body: data }),
  delete: async (id) =>
    makeRequest(`${basePath}${id}/`, { method: "DELETE" }),
  idField,
});

export const contactReasonsAPI = createCrudAPI(
  "/contact-reasons/",
  "reason_id"
);
export const communicationMethodsAPI = createCrudAPI(
  "/communication-methods/",
  "method_id"
);
export const shortSubjectsAPI = createCrudAPI(
  "/short-subjects/",
  "short_subject_id"
);
export const branchesAPI = createCrudAPI("/branches/", "branch_id");
export const productsAPI = createCrudAPI("/products/", "product_id");
export const departmentsAPI = createCrudAPI("/departments/", "department_id");
export const actionTypesAPI = createCrudAPI("/action-types/", "action_type_id");
export const employeesAPI = {
  ...createCrudAPI("/employees/", "employee_no"),
  downloadImportTemplate: async (lang = "ar") => {
    const response = await fetch(
      `${api.baseURL}/employees/import_template/?lang=${encodeURIComponent(lang)}`,
      { method: "GET", credentials: "include" }
    );
    await downloadBlobResponse(response, "employees_import_template.xlsx");
  },
  importFile: async (file, { confirmUpdates = false } = {}) => {
    const form = new FormData();
    form.append("file", file);
    if (confirmUpdates) {
      form.append("confirm_updates", "true");
    }
    return makeRequest("/employees/import/", { method: "POST", body: form });
  },
};

const downloadBlobResponse = async (response, fallbackName) => {
  if (!response.ok) {
    let message = `HTTP error! status: ${response.status}`;
    try {
      const data = await response.json();
      message = data.error || data.detail || message;
    } catch {
      // Keep the status fallback when the body is not JSON.
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] || fallbackName;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

export const replyActionsAPI = {
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/reply-actions/?${queryString}`
      : "/reply-actions/";
    return makeRequest(endpoint);
  },
  saveBulk: async (payload) =>
    makeRequest("/reply-actions/", { method: "POST", body: payload }),
  delete: async (id) =>
    makeRequest(`/reply-actions/${id}/`, { method: "DELETE" }),
};

export const userProfilesAPI = {
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/user-profiles/?${queryString}`
      : "/user-profiles/";
    return makeRequest(endpoint);
  },
  get: async (id) => makeRequest(`/user-profiles/${id}/`),
  getMyProfile: async () => makeRequest("/user-profiles/my_profile/"),
  updateMySettings: async (formData) =>
    makeRequest("/user-profiles/me/settings/", {
      method: "PATCH",
      body: formData,
    }),
  getMyDashboard: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/user-profiles/me/dashboard/?${queryString}`
      : "/user-profiles/me/dashboard/";
    return makeRequest(endpoint);
  },
  getMyParticipatedTickets: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/user-profiles/me/participated-tickets/?${queryString}`
      : "/user-profiles/me/participated-tickets/";
    return makeRequest(endpoint);
  },
  create: async (data) =>
    makeRequest("/user-profiles/", { method: "POST", body: data }),
  update: async (id, data) =>
    makeRequest(`/user-profiles/${id}/`, { method: "PUT", body: data }),
  delete: async (id) =>
    makeRequest(`/user-profiles/${id}/`, { method: "DELETE" }),
};

// Reports API
export const reportsAPI = {
  getDashboard: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/reports/dashboard/?${queryString}`
      : "/reports/dashboard/";
    return makeRequest(endpoint);
  },
  getActionsReport: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/reports/actions/?${queryString}`
      : "/reports/actions/";
    return makeRequest(endpoint);
  },
  exportActionsReport: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/reports/actions/export_excel/?${queryString}`
      : "/reports/actions/export_excel/";
    const response = await fetch(`${api.baseURL}${endpoint}`, {
      method: "GET",
      credentials: "include",
    });
    await downloadBlobResponse(response, "actions_report.xlsx");
  },
};

const appendQueryParams = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") {
          search.append(key, String(item));
        }
      });
      return;
    }
    search.set(key, String(value));
  });
  return search.toString();
};

export const activityLogsAPI = {
  list: async (params = {}) => {
    const queryString = appendQueryParams(params);
    const endpoint = queryString
      ? `/activity-logs/?${queryString}`
      : "/activity-logs/";
    return makeRequest(endpoint);
  },
  eventTypes: async () => makeRequest("/activity-event-types/"),
  purge: async (before) =>
    makeRequest(`/activity-logs/purge/?before=${encodeURIComponent(before)}`, {
      method: "DELETE",
    }),
};

export default {
  authAPI,
  ticketsAPI,
  customersAPI,
  contactReasonsAPI,
  communicationMethodsAPI,
  shortSubjectsAPI,
  branchesAPI,
  productsAPI,
  departmentsAPI,
  actionTypesAPI,
  employeesAPI,
  replyActionsAPI,
  userProfilesAPI,
  reportsAPI,
  activityLogsAPI,
};
